import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Phone, ArrowUpRight, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/pages/Agence";

const CATEGORY_ORDER = [
  "Syndic",
  "Syndic en ligne",
  "AG en visioconférence",
  "ASL",
  "Expertise",
  "Général",
];

export default function Faq() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [openIds, setOpenIds] = useState(new Set());

  useEffect(() => {
    api
      .get("/faq")
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const present = new Set(entries.map((e) => e.category));
    const ordered = CATEGORY_ORDER.filter((c) => present.has(c));
    const extras = [...present].filter((c) => !CATEGORY_ORDER.includes(c));
    return ["Tous", ...ordered, ...extras];
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      const matchCat = activeCategory === "Tous" || e.category === activeCategory;
      if (!matchCat) return false;
      if (!q) return true;
      return (
        e.question.toLowerCase().includes(q) ||
        e.answer.toLowerCase().includes(q)
      );
    });
  }, [entries, search, activeCategory]);

  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      (map[e.category] = map[e.category] || []).push(e);
    }
    return map;
  }, [filtered]);

  const orderedCats = useMemo(() => {
    const cats = Object.keys(grouped);
    cats.sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return cats;
  }, [grouped]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(filtered.map((e) => e.id)));
  const collapseAll = () => setOpenIds(new Set());

  // Auto-expand all matching results when searching
  useEffect(() => {
    if (search.trim().length >= 2) {
      setOpenIds(new Set(filtered.map((e) => e.id)));
    }
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="page-faq">
      <PageHero
        overline="Foire aux questions"
        title="Vos questions, nos réponses."
        subtitle="Plus de 20 réponses classées par thème, mises à jour régulièrement par notre équipe."
      />

      {/* Search & filters */}
      <section className="py-10 lg:py-14 border-b border-[#E2E8F0] sticky top-[96px] lg:top-[144px] bg-[#F9F8F6] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-5 lg:items-center">
            <div className="relative flex-1 max-w-xl">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                data-testid="faq-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tapez votre question (ex. AG visio, expertise, charges…)"
                className="w-full pl-12 pr-4 py-4 border border-[#E2E8F0] bg-white text-base focus:border-[#0F172A] outline-none transition-colors"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="faq-categories">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  data-testid={`faq-cat-${c}`}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
                    activeCategory === c
                      ? "bg-[#0F172A] border-[#0F172A] text-white"
                      : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#B8935A] hover:text-[#0F172A]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {filtered.length > 0 && !loading && (
            <div className="mt-5 flex items-center justify-between text-xs text-[#64748B]">
              <span className="font-overline text-[10px]">
                {filtered.length} {filtered.length > 1 ? "réponses" : "réponse"}
              </span>
              <div className="flex gap-4">
                <button onClick={expandAll} className="hover:text-[#0F172A] underline-offset-2 hover:underline">
                  Tout ouvrir
                </button>
                <button onClick={collapseAll} className="hover:text-[#0F172A] underline-offset-2 hover:underline">
                  Tout fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-[#64748B]">Chargement…</div>
          ) : filtered.length === 0 ? (
            <EmptyState search={search} activeCategory={activeCategory} />
          ) : (
            orderedCats.map((cat) => (
              <div key={cat} className="mb-14" id={`cat-${cat}`}>
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="font-serif text-3xl lg:text-4xl text-[#0F172A]">{cat}</h2>
                  <div className="flex-1 h-px bg-[#E2E8F0]" />
                  <span className="font-overline text-[10px] text-[#94A3B8]">
                    {grouped[cat].length} {grouped[cat].length > 1 ? "questions" : "question"}
                  </span>
                </div>
                <div className="space-y-3">
                  {grouped[cat].map((item) => (
                    <FaqItem
                      key={item.id}
                      item={item}
                      open={openIds.has(item.id)}
                      onToggle={() => toggle(item.id)}
                      highlight={search}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle size={32} className="text-[#D4B584] mx-auto" strokeWidth={1.3} />
          <h2 className="font-serif text-3xl lg:text-4xl mt-6">
            Vous ne trouvez pas votre réponse ?
          </h2>
          <p className="text-[#CBD5E1] mt-5 leading-relaxed max-w-2xl mx-auto">
            Notre équipe répond à toutes vos questions, par téléphone, par mail ou directement
            via Léa, notre assistante virtuelle (bouton en bas à droite).
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href="tel:0687453231"
              data-testid="faq-cta-phone"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#D4B584] text-[#D4B584] hover:bg-[#D4B584] hover:text-[#0F172A] transition-all duration-300 text-sm font-bold tracking-wide"
            >
              <Phone size={14} /> 06 87 45 32 31
            </a>
            <Link
              to="/contact"
              data-testid="faq-cta-contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#B8935A] text-white hover:bg-white hover:text-[#0F172A] transition-all duration-300 text-sm font-bold tracking-wide"
            >
              <MessageCircle size={14} /> Écrire au cabinet
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function highlightText(text, query) {
  if (!query || query.trim().length < 2) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-[#D4B584]/40 text-[#0F172A] px-0.5">
        {p}
      </mark>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

function FaqItem({ item, open, onToggle, highlight }) {
  return (
    <div
      data-testid={`faq-item-${item.id}`}
      className={`bg-white border transition-all duration-300 ${
        open ? "border-[#B8935A] shadow-sm" : "border-[#E2E8F0]"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 p-6 text-left"
        data-testid={`faq-toggle-${item.id}`}
      >
        <h3 className="font-serif text-lg text-[#0F172A] flex-1 leading-snug">
          {highlightText(item.question, highlight)}
        </h3>
        <span
          className={`font-serif text-3xl text-[#B8935A] leading-none transition-transform duration-300 shrink-0 ${
            open ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div
            className="px-6 pb-6 text-[#475569] leading-relaxed text-[0.95rem] gpi-prose"
            dangerouslySetInnerHTML={{ __html: item.answer }}
          />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ search, activeCategory }) {
  return (
    <div className="text-center py-20" data-testid="faq-empty">
      <Search size={32} className="text-[#B8935A] mx-auto" strokeWidth={1.3} />
      <h3 className="font-serif text-2xl text-[#0F172A] mt-6">Aucune réponse ne correspond.</h3>
      <p className="text-[#64748B] mt-3 max-w-md mx-auto">
        {search ? (
          <>Aucun résultat pour <strong>« {search} »</strong>{activeCategory !== "Tous" && <> dans <strong>{activeCategory}</strong></>}.</>
        ) : (
          <>Aucune question dans cette catégorie pour le moment.</>
        )}
      </p>
      <p className="text-sm text-[#94A3B8] mt-6">
        Léa, notre assistante virtuelle, peut peut-être vous aider — bouton en bas à droite.
      </p>
    </div>
  );
}
